<template>
  <div
    class="insured-capital-form"
    data-component="InsuredCapitalForm"
  >
    <h2 class="gl_form-title">
      {{ t(`copy_life_capital-form_title-${answers.forWhom}`) }}
    </h2>

    <CardInfoComponent :icon="capitalToInsuredAlert.icon">
      {{
        maxmin(
          capitalToInsuredAlert.label,
          mandatoryCoverageSlider.max,
          mandatoryCoverageSlider.min
        )
      }}
    </CardInfoComponent>

    <FormField
      v-if="mandatoryCoverageSlider.min && mandatoryCoverageSlider.max"
      :statement="
        t('copy_life_modify-insurance_coverages-title', {
          min: addCommas(mandatoryCoverageSlider.min),
          max: addCommas(mandatoryCoverageSlider.max)
        })
      "
    >
      <AdvantageSliderComponent
        id="coverage-slider"
        v-model="capital"
        :min="mandatoryCoverageSlider.min"
        :max="mandatoryCoverageSlider.max"
        :step="mandatoryCoverageSlider.step"
        :footer-message="
          t(`copy_common_slider-footer_min-max`, {
            min: addCommas(mandatoryCoverageSlider.min),
            max: addCommas(mandatoryCoverageSlider.max)
          })
        "
      />
    </FormField>

    <CardInfoComponent :icon="capitalToInsuredContactAlertIcon">
      <template v-if="hasMediator">
        {{ mediatorAmountAlertText }}
        <div class="mediator-contact">
          <p class="mediator-text">
            {{ mediatorContactPhoneText }}
          </p>
          <p class="mediator-text">
            {{ mediatorContactEmailText }}
          </p>
        </div>
      </template>

      <template v-else>
        {{ maxmin(capitalToInsuredContactAlert.label, mandatoryCoverageSlider.max) }}
        <button
          class="link"
          @click.prevent="openSupportModal"
        >
          {{ capitalToInsuredContactAlert.linkText }}
        </button>
      </template>
    </CardInfoComponent>

    <template v-if="showRevaluation">
      <FormField :statement="t('copy_life_modify-insurance_reevaluation-title')">
        <RadioButtonGroupComponent
          id="reevaluation-type"
          class="reevaluation-type"
          v-model="reevaluationType"
          :value="reevaluationType"
          flex-direction="row"
          flex-wrap="wrap"
          :options="reevaluationOptions"
        />
      </FormField>

      <CardInfoComponent icon="icon-info">
        {{ t('copy_life_modify-insurance_reevaluation-hint') }}
      </CardInfoComponent>
    </template>
  </div>
</template>

<script lang="ts" setup>
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import RadioButtonGroupComponent from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import AdvantageSliderComponent from '@/modules/core/components/utils/AdvantageSliderComponent.vue'
  import FormField from '@/modules/core/components/utils/FormField.vue'
  import GTMEventType from '@/modules/core/models/GTMEventType'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useGTMStore } from '@/modules/core/stores/gtmStore'
  import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'
  import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
  import { useCoverages } from '@/modules/life/common/composables/useCoveragesComposable'
  import type LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
  import { addCommas } from '@/utils/textUtils'
  import { computed, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const supportModalStore = useSupportModalStore()
  const commonStore = useCommonStore()
  const sharedGtmDataStore = useSharedGtmDataStore()
  const gmtStore = useGTMStore()
  const { mandatoryCoverageSlider, loadMandatoryCapitals } = useCoverages()

  defineExpose({ triggerSubmit })
  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: LifeConfigurations
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const capitalToInsuredAlert = props.configurations.tags.capitalToInsuredAlert
  const capitalToInsuredContactAlert = props.configurations.tags.capitalToInsuredContactAlert
  const capitalToInsuredContactAlertMediator =
    props.configurations.tags.capitalToInsuredContactAlertMediator

  const capital = ref(props.answers.coveragesCapital)
  const showRevaluation = computed(
    () => props.configurations.insuranceTypes.mortgage.id === props.answers.insuranceType
  )
  const reevaluationOptions = computed(() =>
    props.configurations.reevaluationOptions.map((option) => ({
      label: option.description,
      value: option.value
    }))
  )
  const reevaluationType = ref<string>(
    props.answers.reevaluationType?.value ?? props.configurations.reevaluationOptions[0].value
  )

  const hasMediator = computed(() => !!commonStore.infoMediator)
  const capitalToInsuredContactAlertIcon = computed(() => {
    if (hasMediator.value) {
      return capitalToInsuredContactAlertMediator?.icon || capitalToInsuredContactAlert.icon
    }
    return capitalToInsuredContactAlert.icon
  })
  const mediatorAmountAlertTemplate = computed(
    () =>
      capitalToInsuredContactAlertMediator?.label ||
      props.configurations.tags.capitalAlertAmountMediator ||
      t('rater.life.capital.alert-amount')
  )
  const mediatorContactPhoneTemplate = computed(
    () =>
      capitalToInsuredContactAlertMediator?.phoneText ||
      props.configurations.tags.capitalAlertMediatorContactPhone ||
      t('rater.life.capital.alert-mediator-contact-phone')
  )
  const mediatorContactEmailTemplate = computed(
    () =>
      capitalToInsuredContactAlertMediator?.emailText ||
      props.configurations.tags.capitalAlertMediatorContactEmail ||
      t('rater.life.capital.alert-mediator-contact-email')
  )
  const mediatorAmountAlertText = computed(() => {
    const max = addCommas(mandatoryCoverageSlider.value.max || 250000)
    const textWithMax = mediatorAmountAlertTemplate.value.replace('{max}', max)
    // If DXP sends phone/email lines inside label, remove them because they are rendered below.
    return textWithMax
      .replace(/\n*\s*Llama al\s*\{phone\}\s*/i, '')
      .replace(/\n*\s*o escr[ií]benos a\s*\{email\}\s*/i, '')
      .trim()
  })
  const mediatorContactPhoneText = computed(() => {
    if (!hasMediator.value || !commonStore.infoMediator) {
      return ''
    }
    const phone = commonStore.infoMediator.mediatorPhone || ''
    return mediatorContactPhoneTemplate.value.replace('{phone}', phone)
  })
  const mediatorContactEmailText = computed(() => {
    if (!hasMediator.value || !commonStore.infoMediator) {
      return ''
    }
    const email = commonStore.infoMediator.mediatorEmail || ''
    return mediatorContactEmailTemplate.value.replace('{email}', email)
  })

  function openSupportModal() {
    gmtStore.sendEvent({
      event: GTMEventType.CALLMEBACK_BTN,
      click_name: 'support-modal-btn',
      click_id: 'support-modal-btn',
      btn_label: t('header.help-text'),
      value: sharedGtmDataStore.policyValue
    })
    supportModalStore.setOpenModal(true)
  }

  function calculateRevaluationType() {
    if (!showRevaluation.value) return null
    return (
      props.configurations.reevaluationOptions.find(
        (option) => option.value === reevaluationType.value
      ) ?? null
    )
  }

  function triggerSubmit() {
    const page = props.nextForm[0]

    const answers = {
      coveragesCapital: capital.value,
      reevaluationType: calculateRevaluationType()
    }

    sharedGtmDataStore.capital = capital.value
    return { page, answers }
  }

  function maxmin(label: string, max: number | null, min?: number | null) {
    const formattedMax = addCommas(max ?? 200000)
    const labelText = label.replace('{max}', formattedMax)
    if (!min) return labelText
    const formattedMin = addCommas(min ?? 0)
    return labelText.replace('{min}', formattedMin)
  }

  function beforeMount() {
    loadMandatoryCapitals(props.answers.insureds[0], props.configurations.capitalOptions)
  }

  onMounted(beforeMount)
</script>

<style lang="scss" scoped>
  .insured-capital-form {
    button {
      margin-top: $spacing-s;
      font-weight: $font-weight-600;
      text-decoration: underline;

      &:hover {
        cursor: pointer;
      }
    }

    .mediator-contact {
      margin-top: $spacing-s;
    }

    .mediator-text {
      font-weight: $font-weight-600;
    }
  }
</style>
